<template>
  <div class="nutrients" :style="nutrientsStyle">
    <DietDetailWeekNutrientSection
      v-if="basicNutrientsVisible"
      :average-active="averageActive"
      :centered-label-style="centeredLabelStyle"
      class="nutrients-basic"
      :nutrient-grid-style="nutrientGridStyle"
      :nutrient-name-style="nutrientNameStyle"
      :nutrients="basicNutrients"
      show-norm-without-target
      title="Podstawowe wartości odżywcze"
    />
    <DietDetailWeekNutrientSection
      v-if="chosenNutrientsVisible"
      :available="globalStore.hasPerm('accounts.diet_microelements')"
      :average-active="averageActive"
      :centered-label-style="centeredLabelStyle"
      class="nutrients-choosen"
      :nutrient-grid-style="nutrientGridStyle"
      :nutrient-name-style="nutrientNameStyle"
      :nutrients="chosenNutrients"
      show-incomplete-data
      title="Wybrane szczegółowe wartości odżywcze"
    >
      <template #unavailable>
        <div
          class="nutrients-choosen-disabled"
          :class="{ extended: averageActive }"
          @click="globalStore.showAppUpgradePlanModal"
        />
      </template>
    </DietDetailWeekNutrientSection>
    <DietDetailWeekNutrientSection
      v-if="A.isNotEmpty(specificNutrientsLimited)"
      id="nutrients-specific"
      :available="globalStore.hasPerm('accounts.diet_microelements')"
      :average-active="averageActive"
      :centered-label-style="centeredLabelStyle"
      class="nutrients-specific"
      :nutrient-grid-style="nutrientGridStyle"
      :nutrient-name-style="nutrientNameStyle"
      :nutrients="specificNutrientsLimited"
      show-incomplete-data
      title="Szczegółowe wartości odżywcze"
    >
      <template #unavailable>
        <div
          class="nutrients-specific-disabled"
          :class="{ extended: averageActive }"
          @click="globalStore.showAppUpgradePlanModal"
        />
      </template>
    </DietDetailWeekNutrientSection>
    <div
      v-if="globalStore.hasPerm('accounts.diet_microelements')"
      class="show-more"
      :style="centeredLabelStyle"
    >
      <StyledButton variant="outline" @click="toggleShowAllNutrients()">
        {{ showAllNutrients ? 'Pokaż mniej ...' : 'Pokaż więcej ...' }}
      </StyledButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { A } from '@mobily/ts-belt'
  import { useDebounceFn, useEventListener } from '@vueuse/core'
  import { computed, ref } from 'vue'

  import DietDetailWeekNutrientSection from '@/components/diets/DietDetailNutrients/DietDetailWeekNutrientSection.vue'
  import { StyledButton } from '@/components/StyledButton'
  import {
    DIET_AVERAGE_COLUMN_EXPANDED_WIDTH_PX,
    DIET_AVERAGE_COLUMN_WIDTH_PX,
    DIET_WEEK_DAYS_COUNT,
  } from '@/const'
  import { useGlobalStore } from '@/store/globalStore'

  import type { NutrientExtended } from '@/utils/nutrients'

  const globalStore = useGlobalStore()
  const props = defineProps<{
    averageActive: boolean
    basicNutrients: NutrientExtended[]
    chosenNutrients: NutrientExtended[]
    specificNutrients: NutrientExtended[]
    visibleDaysCount: number
  }>()

  const showAllNutrients = ref(false)
  const averageActive = computed(() => props.averageActive)
  const averageColumnWidthPx = computed(() =>
    averageActive.value ? DIET_AVERAGE_COLUMN_EXPANDED_WIDTH_PX : DIET_AVERAGE_COLUMN_WIDTH_PX,
  )
  const getVisibleNutrients = (nutrients: NutrientExtended[]): NutrientExtended[] =>
    nutrients.map(nutrient => ({
      ...nutrient,
      differenceValue: nutrient.differenceValue.slice(0, props.visibleDaysCount),
      differenceValuePercent: nutrient.differenceValuePercent.slice(0, props.visibleDaysCount),
      incompleteData: nutrient.incompleteData.slice(0, props.visibleDaysCount),
      normClass: nutrient.normClass.slice(0, props.visibleDaysCount),
      value: nutrient.value.slice(0, props.visibleDaysCount),
      valuePercent: nutrient.valuePercent.slice(0, props.visibleDaysCount),
    })) as NutrientExtended[]
  const basicNutrients = computed(() => getVisibleNutrients(props.basicNutrients))
  const chosenNutrients = computed(() => getVisibleNutrients(props.chosenNutrients))
  const specificNutrients = computed(() => getVisibleNutrients(props.specificNutrients))
  const nutrientsStyle = computed(() => {
    if (props.visibleDaysCount > DIET_WEEK_DAYS_COUNT) {
      return { width: `calc(100% + ${averageColumnWidthPx.value}px)` }
    }

    return { width: '100%' }
  })
  const centeredLabelStyle = computed(() => {
    if (props.visibleDaysCount <= DIET_WEEK_DAYS_COUNT) {
      return { width: '100%' }
    }

    return {
      width: `calc((100% - ${averageColumnWidthPx.value}px) * ${
        DIET_WEEK_DAYS_COUNT / props.visibleDaysCount
      })`,
    }
  })
  const nutrientGridStyle = computed(() => {
    const dayColumns =
      props.visibleDaysCount <= DIET_WEEK_DAYS_COUNT
        ? `repeat(${props.visibleDaysCount}, calc(${100 / DIET_WEEK_DAYS_COUNT}% - ${
            averageColumnWidthPx.value / DIET_WEEK_DAYS_COUNT
          }px)) minmax(0, 1fr)`
        : `repeat(${props.visibleDaysCount}, minmax(0, 1fr))`

    return {
      gridTemplateColumns: `${dayColumns} [average] ${averageColumnWidthPx.value}px`,
    }
  })
  const nutrientNameStyle = computed(() => ({
    gridColumn: '1 / -1',
  }))

  const basicNutrientsVisible = computed(() => {
    return basicNutrients.value.length > 0
  })
  const chosenNutrientsVisible = computed(() => {
    return chosenNutrients.value.length > 0
  })
  const specificNutrientsLimited = computed(() => {
    if (showAllNutrients.value) {
      return specificNutrients.value
    } else {
      return specificNutrients.value.slice(0, 1)
    }
  })

  const toggleShowAllNutrients = () => {
    showAllNutrients.value = !showAllNutrients.value
  }
  const hideAllNutrientsOnScroll = () => {
    const elem = document.getElementById('nutrients-specific')
    if (elem) {
      if (elem.getBoundingClientRect().top > window.innerHeight) {
        showAllNutrients.value = false
      }
    }
  }
  const debounceHideAllNutrientsOnScroll = useDebounceFn(hideAllNutrientsOnScroll, 100)

  useEventListener('scroll', debounceHideAllNutrientsOnScroll)
</script>

<style scoped lang="scss">
  .nutrients {
    box-sizing: border-box;
    min-width: 0;
    max-width: none;
    background: #fff;
  }

  .show-more {
    position: sticky;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 0;
  }

  .nutrients-choosen-disabled,
  .nutrients-choosen-disabled.extended,
  .nutrients-specific-disabled,
  .nutrients-specific-disabled.extended {
    width: 100%;
    background-repeat: no-repeat;
    background-position: center center;
    background-size: 100% 100%;
  }

  .nutrients-choosen-disabled {
    height: 92px;
    background-image: url("@/assets/img/diets/nutrients-choosen.png");
  }

  .nutrients-choosen-disabled.extended {
    height: 92px;
    background-image: url("@/assets/img/diets/nutrients-choosen-extended.png");
  }

  .nutrients-specific-disabled {
    height: 170px;
    background-image: url("@/assets/img/diets/nutrients-specific.png");
  }

  .nutrients-specific-disabled.extended {
    height: 170px;
    background-image: url("@/assets/img/diets/nutrients-specific-extended.png");
  }
</style>
